import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { first, catchError } from 'rxjs/operators';
import * as moment from 'moment';
import { MessageService } from 'primeng/api';
import { AlertService } from '@app/_services';
import { OwnerStmtService } from './owner-stmt.service';
import { InboxMailSMsComponent } from '@app/_components/inbox/inbox-mail-sms/inbox-mail-sms.component';

const STMT_STATUS_PUBLISHED = 7;

/** Minimal statement shape needed to open the mail compose dialog (PDF + owner emails). */
export interface IStatementForMail {
  ownerStmtId: number;
  propertyCode?: string;
  startDate?: any;
  stmtStatus?: number | { value?: number };
}

export interface IOpenStatementMailOptions {
  onLoading?: (loading: boolean) => void;
  onPublishRequired?: (summary: string, detail: string) => void;
}

@Injectable({
  providedIn: 'root'
})
export class OwnerStatementMailService {

  constructor(
    private ownerStmtService: OwnerStmtService,
    private dialog: MatDialog,
    private alertService: AlertService,
    private messageService: MessageService
  ) {}


  openStatementMailCompose(statement: IStatementForMail, options?: IOpenStatementMailOptions): Observable<boolean> {
    const onLoading = options?.onLoading;
    const sent$ = new Subject<boolean>();

    const statusValue = statement?.stmtStatus != null
      ? (typeof statement.stmtStatus === 'object' ? statement.stmtStatus?.value : statement.stmtStatus)
      : undefined;
    // if (statusValue !== STMT_STATUS_PUBLISHED) {
    //   onLoading?.(false);
    //   const summary = 'Cannot send email';
    //   const detail = 'Statement should be in Published status for sending an email.';
    //   if (options?.onPublishRequired) {
    //     options.onPublishRequired(summary, detail);
    //   } else {
    //     this.messageService.add({ severity: 'error', summary, detail, life: 5000 });
    //   }
    //   return;
    // }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;
    dialogConfig.width = '80vw';
    dialogConfig.maxWidth = '95vw';
    // dialogConfig.height = '100vh';

    dialogConfig.data = {
      title: 'New Mail',
      msgTopicId: [statement.ownerStmtId],
      msgTopic: [2600],
      msgTemplateData: null,
      msgTemplateId: 871,
      msgContextId: 8,
      msgSubContextId: 2004
    };

    onLoading?.(true);

    const pdf$ = this.ownerStmtService.getPdfAsBlob(statement.ownerStmtId).pipe(
      first(),
      catchError(() => of(null))
    );
    const ownerEmail$ = this.ownerStmtService.getOwnerEmail(statement.ownerStmtId).pipe(
      first(),
      catchError((err) => {
        console.warn('get-owner-email failed, proceeding without prefill:', err);
        return of({ data: { ownerEmails: [] } });
      })
    );

    forkJoin({ blob: pdf$, ownerRes: ownerEmail$ }).subscribe({
      next: ({ blob, ownerRes }) => {
        if (blob && blob.size > 0) {
          const monthYear = statement.startDate ? moment(statement.startDate).format('MMM YYYY') : 'Statement';
          const pdfFileName = `${statement.propertyCode || 'Statement'} - ${monthYear} statement.pdf`;
          const pdfFile = new File([blob], pdfFileName, { type: 'application/pdf' });
          dialogConfig.data.initialAttachments = [pdfFile];
        } else {
          this.alertService?.error('No PDF received. Opening mail without attachment.');
        }
        const ownerEmails = ownerRes?.data?.ownerEmails;
        dialogConfig.data.ownerEmails = Array.isArray(ownerEmails) ? ownerEmails : [];
        this.openMailDialog(dialogConfig, sent$);
        onLoading?.(false);
      },
      error: (err) => {
        console.error('Error preparing statement for email:', err);
        this.alertService?.error('Failed to prepare statement for email. Opening mail.');
        dialogConfig.data.ownerEmails = [];
        this.openMailDialog(dialogConfig, sent$);
        onLoading?.(false);
      }
    });

    return sent$.asObservable();
  }

  private openMailDialog(dialogConfig: MatDialogConfig, sent$: Subject<boolean>): void {
    const dialogRef = this.dialog.open(InboxMailSMsComponent, dialogConfig);
    let sent = false;

    dialogRef.componentInstance.doAdd.subscribe(() => {
      sent = true;
      dialogRef.close();
    });

    dialogRef.componentInstance.doUpdate.subscribe(() => {
      sent = true;
      dialogRef.close();
    });

    dialogRef.afterClosed().subscribe(() => {
      sent$.next(sent);
      sent$.complete();
    });
  }
}
